import React, { useEffect, useState, useRef, useCallback } from "react";
import { api, CATEGORIES, STATUSES, DECISION_SECTIONS } from "../../lib/api";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import ReviewWorkspace from "../../components/ReviewWorkspace";
import ClientTabs from "./ClientTabs";
import { useActiveClients } from "../../hooks/useActiveClients";
import ReviewWindows from "./ReviewWindows";
import { DocSearchBar, filterDocs } from "../../components/DocSearchBar";
import DocViewer from "../../components/DocViewer";
import { StatusBadge } from "../../components/Shell";
import { ReplyBubble, ReplyToggle } from "../../components/ConversationThread";
import { BlockerToggle } from "../../components/BlockerControl";
import { toast } from "sonner";
import {
  Plus, ChevronDown, Upload, Trash2, Eye, RefreshCw, FileText, X,
  MessageSquareQuote, Check, Paperclip, Youtube, Pencil, History, DollarSign, Download, MessageCircle, Archive, MessagesSquare, CheckCircle2,
} from "lucide-react";

const fieldCls = "rounded-lg px-3 py-2 outline-none border focus:ring-2 focus:ring-[#F97316] text-sm";

function FileSlot({ label, fileRef, onUpload, onRemove, onView, testid }) {
  const inputRef = useRef();
  return (
    <div className="rounded-lg border p-3" style={{ borderColor: "#E5E7EB" }}>
      <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-2">{label}</p>
      {fileRef ? (
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm truncate flex items-center gap-1.5"><FileText size={14} style={{ color: "#F97316" }} />{fileRef.filename}</span>
          <div className="flex gap-1">
            <button data-testid={`${testid}-view`} onClick={onView} className="p-1.5 rounded hover:bg-gray-100 text-[#6B7280]"><Eye size={15} /></button>
            <button data-testid={`${testid}-replace`} onClick={() => inputRef.current.click()} className="p-1.5 rounded hover:bg-gray-100 text-[#6B7280]"><RefreshCw size={15} /></button>
            <button data-testid={`${testid}-remove`} onClick={onRemove} className="p-1.5 rounded hover:bg-gray-100 text-red-500"><Trash2 size={15} /></button>
          </div>
        </div>
      ) : (
        <button data-testid={`${testid}-upload`} onClick={() => inputRef.current.click()}
          className="flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg w-full justify-center border-2 border-dashed text-[#6B7280] hover:border-[#F97316] hover:text-[#F97316] transition-colors"
          style={{ borderColor: "#E5E7EB" }}>
          <Upload size={15} /> Upload
        </button>
      )}
      <input ref={inputRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,image/*"
        onChange={(e) => { if (e.target.files[0]) onUpload(e.target.files[0]); e.target.value = ""; }} />
    </div>
  );
}

// ---------------- Decisions ----------------
const EMPTY_SECTIONS = { the_decision: "", why_this_matters: "", legal_position: "", on_the_ground: "" };

async function saveDecision(docId, initial, payload) {
  if (initial) {
    await api.put(`/documents/${docId}/decisions/${initial.id}`, payload);
    toast.success("Decision updated");
  } else {
    await api.post(`/documents/${docId}/decisions`, payload);
    toast.success("Decision added");
  }
}

function DecisionSectionFields({ docId, sections, setSections }) {
  return DECISION_SECTIONS.map((s) => (
    <div key={s.key}>
      <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-1">{s.label}</p>
      <textarea data-testid={`decision-${s.key.replace(/_/g, "-")}-${docId}`} value={sections[s.key]}
        onChange={(e) => setSections((prev) => ({ ...prev, [s.key]: e.target.value }))}
        placeholder={s.label} rows={3} className={`${fieldCls} w-full resize-y`} style={{ borderColor: "#E5E7EB" }} />
    </div>
  ));
}

function DecisionFormActions({ docId, status, setStatus, onSave, onCancel, editing }) {
  return (
    <div className="flex items-center gap-2">
      <select value={status} onChange={(e) => setStatus(e.target.value)} className={fieldCls} style={{ borderColor: "#E5E7EB" }}>
        <option>Open</option><option>Closed</option>
      </select>
      <button data-testid={`save-decision-${docId}`} onClick={onSave} className="rounded-lg px-4 py-2 font-semibold text-white text-sm" style={{ backgroundColor: "#F97316" }}>
        {editing ? "Update Decision" : "Save Decision"}
      </button>
      {editing && (
        <button data-testid={`cancel-edit-decision-${docId}`} onClick={onCancel} className="rounded-lg px-4 py-2 font-medium text-sm text-[#6B7280] border" style={{ borderColor: "#E5E7EB" }}>Cancel</button>
      )}
    </div>
  );
}

function DecisionForm({ docId, initial, onSaved, onCancel }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [sections, setSections] = useState(() => initial ? {
    the_decision: initial.the_decision || "",
    why_this_matters: initial.why_this_matters || "",
    legal_position: initial.legal_position || "",
    on_the_ground: initial.on_the_ground || "",
  } : EMPTY_SECTIONS);
  const [options, setOptions] = useState((initial?.options || []).join(", "));
  const [status, setStatus] = useState(initial?.status || "Open");

  const save = async () => {
    if (!title.trim()) { toast.error("Decision title required"); return; }
    const opts = options.split(",").map((o) => o.trim()).filter(Boolean);
    await saveDecision(docId, initial, { title, ...sections, options: opts, status });
    onSaved();
  };

  return (
    <div className="space-y-2 mb-4 p-3 bg-white rounded-lg border" style={{ borderColor: "#E5E7EB" }} data-testid={`decision-form-${docId}`}>
      {initial && <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#F97316" }}>Editing decision</p>}
      <input data-testid={`decision-title-${docId}`} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Decision title" className={`${fieldCls} w-full`} style={{ borderColor: "#E5E7EB" }} />
      <DecisionSectionFields docId={docId} sections={sections} setSections={setSections} />
      <input data-testid={`decision-options-${docId}`} value={options} onChange={(e) => setOptions(e.target.value)} placeholder="Options, comma separated (e.g. Monthly, Quarterly, Annually)" className={`${fieldCls} w-full`} style={{ borderColor: "#E5E7EB" }} />
      <DecisionFormActions docId={docId} status={status} setStatus={setStatus} onSave={save} onCancel={onCancel} editing={!!initial} />
    </div>
  );
}

function DecisionResponseRow({ r, onAddToAccount }) {
  return (
    <div className="text-sm py-1 flex items-center gap-2 flex-wrap">
      <span className="text-[#1A1A1A]">{r.response_text}</span>
      <span className="text-xs text-[#6B7280]">— {r.name}, {new Date(r.created_at).toLocaleDateString()}</span>
      {r.post_window && (
        <>
          <span data-testid={`post-window-response-${r.id}`} className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
            style={{ backgroundColor: "#FEF0E6", color: "#F97316" }}>
            Post-window
          </span>
          <button data-testid={`add-response-to-account-${r.id}`} onClick={() => onAddToAccount(r)}
            className="flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-lg text-white" style={{ backgroundColor: "#F97316" }}>
            <DollarSign size={11} /> Add to Billing
          </button>
        </>
      )}
    </div>
  );
}

function DecisionResponses({ dec, docId, reload, onAddToAccount }) {
  const responses = dec.responses || [];
  const sendReply = async (text) => {
    await api.post(`/documents/${docId}/decisions/${dec.id}/responses`, { name: "360 Safety Systems", response_text: text });
    toast.success("Reply sent — it will show in the Review portal");
    reload();
  };
  return (
    <div className="mt-2 pl-3 border-l-2 space-y-1.5" style={{ borderColor: "#F97316" }}>
      <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-1">Conversation ({responses.length})</p>
      {responses.length === 0 && <p className="text-xs text-[#9CA3AF]">No responses yet.</p>}
      {responses.map((r) => r.author_role === "admin"
        ? <ReplyBubble key={r.id} author={r.name} isAdmin text={r.response_text} date={r.created_at} testId={`admin-decision-reply-${r.id}`} />
        : <DecisionResponseRow key={r.id} r={r} onAddToAccount={onAddToAccount} />)}
      <ReplyToggle onSend={sendReply} testPrefix={`decision-reply-${dec.id}`} placeholder="Reply to this decision thread..." />
    </div>
  );
}

function DecisionItem({ dec, docId, clientId, onEdit, onRemoved, reload }) {
  const toggleStatus = async () => {
    await api.put(`/documents/${docId}/decisions/${dec.id}`, { status: dec.status === "Open" ? "Closed" : "Open" });
    reload();
  };
  const remove = async () => {
    await api.delete(`/documents/${docId}/decisions/${dec.id}`);
    toast.success("Decision removed");
    onRemoved(dec.id);
  };
  const addToAccount = async (r) => {
    try {
      await api.post(`/clients/${clientId}/account/from-decision/${docId}/${r.id}`);
      toast.success("Added to billing as a requested variation");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add to billing");
    }
  };

  return (
    <div data-testid={`decision-item-${dec.id}`} className="bg-white rounded-lg border p-3" style={{ borderColor: "#E5E7EB" }}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium">{dec.title}
            <span className="ml-2 text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: dec.status === "Open" ? "#FEF0E6" : "#E8EDF4", color: dec.status === "Open" ? "#F97316" : "#0A1F44" }}>{dec.status}</span>
          </p>
          {dec.the_decision && <p className="text-sm text-[#6B7280] mt-1">{dec.the_decision}</p>}
          {dec.options?.length > 0 && <p className="text-xs text-[#6B7280] mt-1">Options: {dec.options.join(" · ")}</p>}
        </div>
        <div className="flex gap-1 flex-shrink-0">
          <button data-testid={`edit-decision-${dec.id}`} onClick={onEdit} className="p-1.5 rounded hover:bg-gray-100 text-[#6B7280]" title="Edit decision"><Pencil size={15} /></button>
          <button data-testid={`toggle-decision-${dec.id}`} onClick={toggleStatus} className="p-1.5 rounded hover:bg-gray-100 text-[#6B7280]" title="Toggle Open/Closed"><Check size={15} /></button>
          <button data-testid={`delete-decision-${dec.id}`} onClick={remove} className="p-1.5 rounded hover:bg-gray-100 text-red-500"><Trash2 size={15} /></button>
        </div>
      </div>
      <DecisionResponses dec={dec} docId={docId} reload={reload} onAddToAccount={addToAccount} />
    </div>
  );
}

function DecisionsManager({ doc, reload }) {
  // editing: undefined = form closed, null = adding, decision object = editing
  const [editing, setEditing] = useState();
  const formOpen = editing !== undefined;
  const close = () => setEditing(undefined);
  const startAdd = () => setEditing(editing === null ? undefined : null);
  const handleRemoved = (id) => {
    if (editing && editing.id === id) close();
    reload();
  };

  return (
    <div className="mt-4 rounded-lg border p-4" style={{ borderColor: "#E5E7EB", backgroundColor: "#F9FAFB" }}>
      <div className="flex items-center justify-between mb-3">
        <p className="font-heading font-semibold flex items-center gap-2"><MessageSquareQuote size={16} style={{ color: "#F97316" }} /> Client Decisions</p>
        <button data-testid={`add-decision-${doc.id}`} onClick={startAdd}
          className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg text-white" style={{ backgroundColor: "#0A1F44" }}>
          <Plus size={15} /> Add New Decision
        </button>
      </div>

      {formOpen && (
        <DecisionForm key={editing?.id || "new"} docId={doc.id} initial={editing}
          onSaved={() => { close(); reload(); }} onCancel={close} />
      )}

      <div className="space-y-3">
        {(doc.decisions || []).length === 0 && <p className="text-sm text-[#6B7280]">No decisions yet.</p>}
        {(doc.decisions || []).map((dec) => (
          <DecisionItem key={dec.id} dec={dec} docId={doc.id} clientId={doc.client_id} reload={reload}
            onEdit={() => setEditing(dec)} onRemoved={handleRemoved} />
        ))}
      </div>
    </div>
  );
}

// ---------------- Document row panels ----------------
function StatusNotePanel({ doc, targetStatus, onConfirm, onCancel }) {
  const [note, setNote] = useState("");
  return (
    <div data-testid={`amend-note-panel-${doc.id}`} className="mb-4 p-3 rounded-lg border bg-white" style={{ borderColor: "#E5E7EB" }}>
      <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-2">
        Moving to <span style={{ color: "#F97316" }}>{targetStatus}</span> — system stamp note (optional)
      </p>
      <input data-testid={`amend-note-input-${doc.id}`} value={note} onChange={(e) => setNote(e.target.value)}
        placeholder="Short note recorded in the change history & audit, e.g. v2 SDS storage wording" className={`${fieldCls} w-full`} style={{ borderColor: "#E5E7EB" }} />
      <div className="flex gap-2 mt-2">
        <button data-testid={`amend-confirm-${doc.id}`} onClick={() => onConfirm(note)} className="rounded-lg px-4 py-2 font-semibold text-white text-sm" style={{ backgroundColor: "#F97316" }}>Set to {targetStatus}</button>
        <button data-testid={`amend-cancel-${doc.id}`} onClick={onCancel} className="rounded-lg px-4 py-2 font-medium text-sm text-[#6B7280] border" style={{ borderColor: "#E5E7EB" }}>Cancel</button>
      </div>
    </div>
  );
}

function VideoPanel({ doc, reload }) {
  const [videoUrl, setVideoUrl] = useState(doc.video_url || "");
  const saveVideo = async () => {
    await api.put(`/documents/${doc.id}`, { video_url: videoUrl.trim() });
    toast.success(videoUrl.trim() ? "Video link saved" : "Video link removed");
    reload();
  };
  return (
    <div className="mt-3 rounded-lg border p-3" style={{ borderColor: "#E5E7EB" }}>
      <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-2 flex items-center gap-1.5">
        <Youtube size={14} style={{ color: "#F97316" }} /> Video URL (YouTube)
      </p>
      <div className="flex gap-2">
        <input data-testid={`video-url-input-${doc.id}`} value={videoUrl}
          onChange={(e) => setVideoUrl(e.target.value)}
          placeholder="https://www.youtube.com/watch?v=..."
          className={`${fieldCls} flex-1`} style={{ borderColor: "#E5E7EB" }} />
        <button data-testid={`save-video-${doc.id}`} onClick={saveVideo}
          className="rounded-lg px-4 py-2 font-semibold text-white text-sm" style={{ backgroundColor: "#0A1F44" }}>Save</button>
      </div>
      {doc.video_url && (
        <p className="text-xs mt-1.5" style={{ color: "#10B981" }}>
          Video attached — a “Watch Video” button shows in the Manager &amp; Team portals.
        </p>
      )}
    </div>
  );
}

const PORTAL_OPTIONS = [
  { key: "review", label: "Review Portal" },
  { key: "manager", label: "Manager Portal" },
  { key: "team", label: "Team Portal" },
];

const PORTAL_SHORT = [
  { key: "review", short: "R", label: "Review" },
  { key: "manager", short: "M", label: "Manager" },
  { key: "team", short: "T", label: "Team" },
];

function PortalSummary({ attachments }) {
  const atts = attachments || [];
  if (!atts.length) {
    return <span className="text-xs text-[#D1D5DB]" title="No supporting attachments" data-testid="portal-summary-none">—</span>;
  }
  const union = new Set();
  atts.forEach((a) => (a.portals || PORTAL_OPTIONS.map((p) => p.key)).forEach((p) => union.add(p)));
  return (
    <div className="flex items-center gap-1" data-testid="portal-summary"
      title="Portals where this document's supporting attachments are visible">
      {PORTAL_SHORT.map((p) => {
        const on = union.has(p.key);
        return (
          <span key={p.key} data-testid={`portal-chip-${p.key}-${on ? "on" : "off"}`}
            title={`${p.label} Portal: ${on ? "visible" : "hidden"}`}
            className="w-5 h-5 rounded text-[10px] font-bold flex items-center justify-center"
            style={on ? { backgroundColor: "#0A1F44", color: "#fff" } : { backgroundColor: "#F3F4F6", color: "#D1D5DB" }}>
            {p.short}
          </span>
        );
      })}
    </div>
  );
}

function PortalCheckboxes({ selected, onToggle, idPrefix }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      {PORTAL_OPTIONS.map((p) => (
        <label key={p.key} className="flex items-center gap-1 text-xs text-[#374151] cursor-pointer">
          <input type="checkbox" data-testid={`${idPrefix}-portal-${p.key}`} className="accent-[#F97316]"
            checked={selected.includes(p.key)} onChange={() => onToggle(p.key)} />
          {p.label}
        </label>
      ))}
    </div>
  );
}

function PendingAttachments({ docId, pending, setPending, uploading, onUpload }) {
  const togglePortal = (uid, key) => setPending((arr) => arr.map((x) => {
    if (x.uid !== uid) return x;
    const has = x.portals.includes(key);
    return { ...x, portals: has ? x.portals.filter((k) => k !== key) : [...x.portals, key] };
  }));
  return (
    <div className="space-y-2 mb-3 p-2 rounded-lg" style={{ backgroundColor: "#FEF0E6" }} data-testid={`pending-attachments-${docId}`}>
      <p className="text-xs text-[#6B7280]">Name each file and choose which portals it appears in, then upload:</p>
      {pending.map((p) => (
        <div key={p.uid} className="bg-white rounded-md p-2 border space-y-1.5" style={{ borderColor: "#E5E7EB" }}>
          <div className="flex items-center gap-2">
            <input value={p.name}
              onChange={(e) => setPending((arr) => arr.map((x) => x.uid === p.uid ? { ...x, name: e.target.value } : x))}
              className="flex-1 rounded px-2 py-1.5 text-sm border bg-white" style={{ borderColor: "#E5E7EB" }}
              placeholder="Display name (e.g. Emergency Response Poster)" />
            <button onClick={() => setPending((arr) => arr.filter((x) => x.uid !== p.uid))} className="text-red-500 p-1"><X size={14} /></button>
          </div>
          <PortalCheckboxes selected={p.portals} onToggle={(k) => togglePortal(p.uid, k)} idPrefix={`pending-${p.uid}`} />
        </div>
      ))}
      <button data-testid={`upload-attachments-${docId}`} onClick={onUpload} disabled={uploading}
        className="rounded-lg px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-60" style={{ backgroundColor: "#F97316" }}>
        {uploading ? "Uploading..." : `Upload ${pending.length} file(s)`}
      </button>
    </div>
  );
}

function AdminAttachmentRow({ a, onView, onDelete, onUpdatePortals }) {
  const [editing, setEditing] = useState(false);
  const [sel, setSel] = useState(a.portals || PORTAL_OPTIONS.map((p) => p.key));
  const [saving, setSaving] = useState(false);
  const toggle = (k) => setSel((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k]);
  const save = async () => {
    if (!sel.length) { toast.error("Select at least one portal"); return; }
    setSaving(true);
    try { await onUpdatePortals(a.id, sel); setEditing(false); } finally { setSaving(false); }
  };
  const current = a.portals || PORTAL_OPTIONS.map((p) => p.key);
  return (
    <div className="text-sm py-1.5 border-b last:border-0" style={{ borderColor: "#F3F4F6" }} data-testid={`admin-attachment-${a.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 truncate"><FileText size={13} />{a.display_name || a.filename}</span>
        <div className="flex gap-1 flex-shrink-0">
          <button data-testid={`att-edit-${a.id}`} title="Edit portal visibility" onClick={() => setEditing((e) => !e)} className="p-1 rounded hover:bg-gray-100 text-[#6B7280]"><Pencil size={14} /></button>
          <button onClick={() => onView({ file: a, title: a.display_name || a.filename })} className="p-1 rounded hover:bg-gray-100 text-[#6B7280]"><Eye size={14} /></button>
          <button onClick={() => onDelete(a.id)} className="p-1 rounded hover:bg-gray-100 text-red-500"><Trash2 size={14} /></button>
        </div>
      </div>
      {!editing ? (
        <div className="flex flex-wrap gap-1 mt-1 ml-5" data-testid={`att-portals-${a.id}`}>
          {PORTAL_OPTIONS.filter((p) => current.includes(p.key)).map((p) => (
            <span key={p.key} className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: "#E8EDF4", color: "#0A1F44" }}>{p.label}</span>
          ))}
        </div>
      ) : (
        <div className="mt-1.5 ml-5 flex flex-wrap items-center gap-3">
          <PortalCheckboxes selected={sel} onToggle={toggle} idPrefix={`att-${a.id}`} />
          <button data-testid={`att-save-${a.id}`} onClick={save} disabled={saving}
            className="text-xs font-semibold px-3 py-1 rounded text-white disabled:opacity-60" style={{ backgroundColor: "#F97316" }}>
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      )}
    </div>
  );
}

function AttachmentList({ attachments, onView, onDelete, onUpdatePortals }) {
  if (!attachments.length) return <p className="text-sm text-[#9CA3AF]">None.</p>;
  return (
    <div className="space-y-1">
      {attachments.map((a) => (
        <AdminAttachmentRow key={a.id} a={a} onView={onView} onDelete={onDelete} onUpdatePortals={onUpdatePortals} />
      ))}
    </div>
  );
}

function AttachmentsPanel({ doc, reload, onView }) {
  const [pending, setPending] = useState([]); // [{uid, file, name}]
  const [uploading, setUploading] = useState(false);
  const attachRef = useRef();

  const pickFiles = (files) => {
    const arr = Array.from(files).map((f) => ({ uid: `${Date.now()}-${Math.random().toString(36).slice(2)}`, file: f, name: f.name, portals: PORTAL_OPTIONS.map((p) => p.key) }));
    setPending((p) => [...p, ...arr]);
  };
  const uploadPending = async () => {
    if (!pending.length) return;
    if (pending.some((p) => p.portals.length === 0)) { toast.error("Each file needs at least one portal selected"); return; }
    setUploading(true);
    try {
      for (const p of pending) {
        const fd = new FormData();
        fd.append("file", p.file);
        fd.append("display_name", p.name);
        fd.append("portals", p.portals.join(","));
        await api.post(`/documents/${doc.id}/attachments`, fd);
      }
      toast.success(`${pending.length} attachment(s) uploaded`);
      setPending([]);
      reload();
    } catch {
      toast.error("Upload failed");
    } finally { setUploading(false); }
  };
  const delAttachment = async (id) => { await api.delete(`/documents/${doc.id}/attachments/${id}`); reload(); };
  const updatePortals = async (id, portals) => {
    await api.put(`/documents/${doc.id}/attachments/${id}`, { portals });
    toast.success("Attachment visibility updated");
    reload();
  };

  return (
    <div className="mt-3 rounded-lg border p-3" style={{ borderColor: "#E5E7EB" }}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Supporting Attachments</p>
        <button data-testid={`add-attachment-${doc.id}`} onClick={() => attachRef.current.click()} className="flex items-center gap-1.5 text-sm text-[#F97316] font-medium"><Paperclip size={14} /> Add files</button>
        <input ref={attachRef} type="file" multiple className="hidden" accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,image/*"
          onChange={(e) => { if (e.target.files.length) pickFiles(e.target.files); e.target.value = ""; }} />
      </div>

      {pending.length > 0 && (
        <PendingAttachments docId={doc.id} pending={pending} setPending={setPending} uploading={uploading} onUpload={uploadPending} />
      )}

      <AttachmentList attachments={doc.attachments || []} onView={onView} onDelete={delAttachment} onUpdatePortals={updatePortals} />
    </div>
  );
}

function eventLabel(e) {
  if (e.event === "category_change") return `Category: ${e.from_status} → ${e.to_status}`;
  if (e.event === "returned_for_amendment") return `Returned for amendment${e.note ? ` (${e.note})` : ""}`;
  if (e.event === "amended") return `Amended${e.note ? ` (${e.note})` : ""}`;
  if (e.event === "proceed_to_consultation") return "Proceeded to consultation";
  return `Status: ${e.from_status || "—"} → ${e.to_status}${e.note ? ` (${e.note})` : ""}`;
}

function HistoryList({ events }) {
  if (events === null) return <p className="text-xs text-[#9CA3AF]">Loading...</p>;
  if (events.length === 0) return <p className="text-xs text-[#9CA3AF]">No changes recorded.</p>;
  return events.map((e) => (
    <div key={e.id} className="flex items-center justify-between gap-2 text-xs py-1 border-b last:border-b-0" style={{ borderColor: "#F3F4F6" }}>
      <span className="text-[#1A1A1A]">{eventLabel(e)} <span className="text-[#9CA3AF]">· {e.by}</span></span>
      <span className="text-[#6B7280] flex-shrink-0">{new Date(e.created_at).toLocaleString()}</span>
    </div>
  ));
}

function ChangeHistory({ doc }) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState(null);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && events === null) {
      api.get(`/documents/${doc.id}/events`).then(({ data }) => setEvents(data));
    }
  };

  return (
    <div className="mt-3 rounded-lg border p-3" style={{ borderColor: "#E5E7EB" }}>
      <button data-testid={`change-history-toggle-${doc.id}`} onClick={toggle}
        className="w-full flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-[#6B7280]">
        <span className="flex items-center gap-1.5"><History size={13} /> Change History</span>
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-2 space-y-1" data-testid={`change-history-list-${doc.id}`}>
          <HistoryList events={events} />
        </div>
      )}
    </div>
  );
}

function UnreadItem({ label, text, byline, onRead, testId }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b last:border-b-0" style={{ borderColor: "#FDE8D8" }} data-testid={testId}>
      <div className="min-w-0">
        <p className="text-sm text-[#1A1A1A]">
          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded mr-1.5" style={{ backgroundColor: "#FEF0E6", color: "#F97316" }}>{label}</span>
          {text}
        </p>
        <p className="text-xs text-[#6B7280] mt-0.5">{byline}</p>
      </div>
      <button onClick={onRead} data-testid={`${testId}-mark-read`}
        className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg text-white flex-shrink-0" style={{ backgroundColor: "#0A1F44" }}>
        <Check size={12} /> Mark read
      </button>
    </div>
  );
}

function UnreadActivity({ doc, reload }) {
  if (!doc.unread_count) return null;
  const markFeedback = async (id) => { await api.post(`/feedback/${id}/read`); toast.success("Marked as read"); reload(); };
  const markResponse = async (id) => { await api.post(`/documents/${doc.id}/responses/${id}/read`); toast.success("Marked as read"); reload(); };
  return (
    <div className="rounded-lg border p-3 mb-4" style={{ backgroundColor: "#FFF9F4", borderColor: "#FDBA74" }} data-testid={`unread-activity-${doc.id}`}>
      <p className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5 mb-1" style={{ color: "#9A3412" }}>
        <MessageCircle size={13} /> New activity — unread ({doc.unread_count})
      </p>
      {(doc.unread_feedback || []).map((f) => (
        <UnreadItem key={f.id} label={f.source_role === "worker" ? "Question" : "Feedback"} text={f.feedback_text}
          byline={`${f.name} · ${new Date(f.created_at).toLocaleString()}`}
          onRead={() => markFeedback(f.id)} testId={`unread-feedback-${f.id}`} />
      ))}
      {(doc.unread_responses || []).map((r) => (
        <UnreadItem key={r.id} label="Decision answer" text={r.response_text}
          byline={`${r.name} · ${new Date(r.created_at).toLocaleString()}`}
          onRead={() => markResponse(r.id)} testId={`unread-response-${r.id}`} />
      ))}
      <p className="text-[11px] mt-2" style={{ color: "#9A3412" }}>
        Anything you leave unread keeps the message icon on this document — including next time you log in.
      </p>
    </div>
  );
}

function FlagNewToggle({ doc, reload }) {
  const toggle = async (e) => {
    e.stopPropagation();
    await api.put(`/documents/${doc.id}`, { flag_new: !doc.flag_new });
    toast.success(doc.flag_new
      ? "NEW! marker removed"
      : "NEW! marker on — workers and managers will see a purple NEW! icon on this document");
    reload();
  };
  return (
    <button data-testid={`flag-new-toggle-${doc.id}`} onClick={toggle} aria-pressed={!!doc.flag_new}
      title="Toggle the purple NEW! marker shown next to this document in the Worker and Manager portals"
      className="text-[10px] font-extrabold tracking-wide px-2 py-0.5 rounded-full flex-shrink-0 border transition-colors"
      style={doc.flag_new
        ? { backgroundColor: "#7C3AED", color: "#fff", borderColor: "#7C3AED" }
        : { color: "#9CA3AF", borderColor: "#E5E7EB", backgroundColor: "#fff" }}>
      NEW!
    </button>
  );
}

function DocRowHeader({ doc, open, onToggle, reload, onWorkspace }) {
  return (
    <div className="grid grid-cols-12 items-center px-4 py-3 gap-2 hover:bg-gray-50 cursor-pointer" onClick={onToggle}>
      <div className="col-span-3 font-medium flex items-center gap-2 min-w-0">
        <ChevronDown size={16} className={`text-[#6B7280] transition-transform ${open ? "rotate-180" : ""}`} />
        <span className="truncate">{doc.title}</span>
        <button data-testid={`header-workspace-${doc.id}`} title="Open the split-screen review workspace"
          onClick={(e) => { e.stopPropagation(); onWorkspace(); }}
          className="p-1 rounded-md text-[#9CA3AF] hover:text-[#F97316] hover:bg-orange-50 transition-colors flex-shrink-0">
          <MessagesSquare size={14} />
        </button>
        <FlagNewToggle doc={doc} reload={reload} />
        <BlockerToggle flagged={doc.blocker} note={doc.blocker_note} testPrefix={`blocker-${doc.id}`}
          onSave={async (flag, note) => {
            await api.put(`/documents/${doc.id}`, { blocker: flag, blocker_note: note });
            toast.success(flag ? "Blocker flagged — the client will see a warning in their Review portal" : "Blocker removed");
            reload();
          }} />
        {doc.unread_count > 0 && (
          <span data-testid={`unread-badge-${doc.id}`} title={`${doc.unread_count} unread feedback / decision answer(s)`}
            className="flex items-center gap-1 text-[11px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: "#F97316", color: "#fff" }}>
            <MessageCircle size={11} /> {doc.unread_count}
          </span>
        )}
      </div>
      <div className="col-span-2 text-sm text-[#6B7280]">{doc.category}</div>
      <div className="col-span-3 flex items-center gap-2">
        <StatusBadge status={doc.status} />
        {doc.consultation_proceeded_at && doc.status === "In Review" && (
          <span data-testid={`ready-consultation-${doc.id}`}
            title={`Client clicked "Proceed to consultation" in the Review portal on ${new Date(doc.consultation_proceeded_at).toLocaleString()}`}
            className="flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap"
            style={{ backgroundColor: "#DCFCE7", color: "#059669" }}>
            <CheckCircle2 size={11} /> Ready for consultation
          </span>
        )}
      </div>
      <div className="col-span-2"><PortalSummary attachments={doc.attachments} /></div>
      <div className="col-span-1 text-center">{doc.main_file ? <Check size={16} className="mx-auto" style={{ color: "#10B981" }} /> : <X size={16} className="mx-auto text-[#D1D5DB]" />}</div>
      <div className="col-span-1 text-center">{doc.summary_sheet ? <Check size={16} className="mx-auto" style={{ color: "#10B981" }} /> : <X size={16} className="mx-auto text-[#D1D5DB]" />}</div>
    </div>
  );
}

function DownloadToggle({ doc, reload }) {
  const toggle = async () => {
    await api.put(`/documents/${doc.id}`, { downloadable: !doc.downloadable });
    toast.success(doc.downloadable
      ? "Downloads turned off — clients are back to view-only for this document"
      : "Downloads turned on — clients can now download this document and its attachments");
    reload();
  };
  return (
    <button data-testid={`download-toggle-${doc.id}`} onClick={toggle}
      title="When on, this document, its summary and attachments can be downloaded from the Worker, Manager and Review portals"
      className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors"
      style={doc.downloadable
        ? { backgroundColor: "#DCFCE7", borderColor: "#10B981", color: "#059669" }
        : { borderColor: "#E5E7EB", color: "#6B7280" }}>
      <Download size={13} />
      {doc.downloadable ? "Client downloads: On" : "Client downloads: Off"}
    </button>
  );
}

function DocRowControls({ doc, reload, onDelete, onStatusChange }) {
  const changeStatus = (status) => {
    if (status !== doc.status) onStatusChange(status);
  };
  const changeCategory = async (category) => {
    await api.put(`/documents/${doc.id}`, { category });
    toast.success(`Category changed to ${category}`);
    reload();
  };
  return (
    <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <label className="text-sm text-[#6B7280]">Status</label>
          <select data-testid={`status-select-${doc.id}`} value={doc.status} onChange={(e) => changeStatus(e.target.value)} className={fieldCls} style={{ borderColor: "#E5E7EB" }}>
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-[#6B7280]">Category</label>
          <select data-testid={`category-select-${doc.id}`} value={doc.category} onChange={(e) => changeCategory(e.target.value)} className={fieldCls} style={{ borderColor: "#E5E7EB" }}>
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <DownloadToggle doc={doc} reload={reload} />
      </div>
      <button data-testid={`delete-doc-${doc.id}`} onClick={onDelete} className="flex items-center gap-1.5 text-sm text-red-500 px-3 py-1.5 rounded-lg hover:bg-red-50"><Trash2 size={15} /> Delete document</button>
    </div>
  );
}

function DocFileSlots({ doc, reload, onView }) {
  const setFile = async (field, file) => {
    const fd = new FormData(); fd.append("file", file);
    await api.post(`/documents/${doc.id}/${field}`, fd);
    toast.success("File uploaded"); reload();
  };
  const removeFile = async (field) => { await api.delete(`/documents/${doc.id}/${field}`); toast.success("File removed"); reload(); };
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <FileSlot testid={`main-${doc.id}`} label="Main Document" fileRef={doc.main_file}
        onUpload={(f) => setFile("main-file", f)} onRemove={() => removeFile("main-file")} onView={() => onView({ file: doc.main_file, title: doc.title })} />
      <FileSlot testid={`summary-${doc.id}`} label="Summary Sheet" fileRef={doc.summary_sheet}
        onUpload={(f) => setFile("summary-sheet", f)} onRemove={() => removeFile("summary-sheet")} onView={() => onView({ file: doc.summary_sheet, title: `${doc.title} — Summary` })} />
    </div>
  );
}

function CommentaryActions({ docId, onOpenLive, onPackage }) {
  return (
    <div className="flex gap-2">
      <button data-testid={`admin-open-workspace-${docId}`} onClick={onOpenLive}
        className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors hover:border-[#F97316] hover:text-[#F97316]"
        style={{ borderColor: "#E5E7EB", color: "#6B7280" }}>
        <Eye size={13} /> Open workspace
      </button>
      <button data-testid={`package-commentary-${docId}`} onClick={onPackage}
        className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg text-white"
        style={{ backgroundColor: "#0A1F44" }}>
        <Archive size={13} /> Package Commentary
      </button>
    </div>
  );
}

function ReviewHistoryList({ packages, onOpen }) {
  if (!packages.length) return null;
  return (
    <div className="mt-2">
      <p className="text-[11px] font-semibold text-[#9CA3AF] mb-1">Review History</p>
      <div className="space-y-1">
        {packages.map((p) => (
          <button key={p.id} data-testid={`review-history-${p.id}`} onClick={() => onOpen(p.id)}
            className="w-full flex items-center justify-between text-left text-xs px-2.5 py-2 rounded-lg border hover:border-[#F97316] transition-colors"
            style={{ borderColor: "#E5E7EB" }}>
            <span className="font-medium text-[#1A1A1A]">
              Packaged {new Date(p.created_at).toLocaleString()} · {p.status_at_package}
            </span>
            <span className="text-[#6B7280]">{p.comment_count} comments · {p.decision_count} decisions · {p.feedback_count} items</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function CommentaryPanel({ doc, reload, onOpenLive }) {
  const [packages, setPackages] = useState([]);
  const [historyWs, setHistoryWs] = useState(null);
  const [confirmPkg, setConfirmPkg] = useState(false);

  const loadPackages = useCallback(() => {
    api.get(`/documents/${doc.id}/review-packages`).then(({ data }) => setPackages(data)).catch(() => {});
  }, [doc.id]);
  useEffect(() => { loadPackages(); }, [loadPackages]);

  const doPackage = async () => {
    setConfirmPkg(false);
    try {
      const { data } = await api.post(`/documents/${doc.id}/package-commentary`);
      toast.success(`Commentary packaged — ${data.comments} comment(s), ${data.decisions} decision(s), ${data.feedback} item(s) archived to Review History`);
      loadPackages(); reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not package commentary");
    }
  };

  const openHistory = async (pkgId) => {
    const { data } = await api.get(`/review-packages/${pkgId}`);
    setHistoryWs(data);
  };

  return (
    <div className="mt-3 rounded-lg border p-3 bg-white" style={{ borderColor: "#E5E7EB" }}>
      <ConfirmDialog open={confirmPkg}
        message="Package the commentary for this document? The thread will be frozen as a read-only Review History record and removed from all client-facing views."
        confirmLabel="Package" onConfirm={doPackage} onCancel={() => setConfirmPkg(false)}
        testId={`package-confirm-${doc.id}`} />
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Review Commentary</p>
        <CommentaryActions docId={doc.id} onOpenLive={onOpenLive} onPackage={() => setConfirmPkg(true)} />
      </div>
      <ReviewHistoryList packages={packages} onOpen={openHistory} />
      {historyWs && (
        <ReviewWorkspace doc={doc} clientId={doc.client_id} role="admin"
          historyPackage={historyWs} onClose={() => setHistoryWs(null)} />
      )}
    </div>
  );
}

function DocumentRow({ doc, reload, onView }) {
  const [open, setOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [wsOpen, setWsOpen] = useState(false);

  const confirmStatus = async (note) => {
    const target = statusTarget;
    setStatusTarget(null);
    await api.put(`/documents/${doc.id}`, { status: target, amendment_note: (note || "").trim() });
    toast.success(`Status set to ${target}`);
    reload();
  };
  const reallyDelDoc = async () => {
    setConfirmDelete(false);
    await api.delete(`/documents/${doc.id}`);
    toast.success("Document deleted");
    reload();
  };

  return (
    <div className="border-t" style={{ borderColor: "#E5E7EB" }} data-testid={`admin-doc-${doc.id}`}>
      <DocRowHeader doc={doc} open={open} onToggle={() => setOpen((o) => !o)} reload={reload} onWorkspace={() => setWsOpen(true)} />

      {open && (
        <div className="px-4 pb-5 pt-1 bg-[#F9FAFB]" onClick={(e) => e.stopPropagation()}>
          <UnreadActivity doc={doc} reload={reload} />
          <DocRowControls doc={doc} reload={reload} onDelete={() => setConfirmDelete(true)} onStatusChange={setStatusTarget} />

          {statusTarget && <StatusNotePanel doc={doc} targetStatus={statusTarget} onConfirm={confirmStatus} onCancel={() => setStatusTarget(null)} />}

          <ConfirmDialog
            open={confirmDelete}
            message="Delete this document?"
            confirmLabel="Delete"
            onConfirm={reallyDelDoc}
            onCancel={() => setConfirmDelete(false)}
            testId={`doc-confirm-${doc.id}`}
          />

          <CommentaryPanel doc={doc} reload={reload} onOpenLive={() => setWsOpen(true)} />
          <DocFileSlots doc={doc} reload={reload} onView={onView} />
          <VideoPanel doc={doc} reload={reload} />
          <AttachmentsPanel doc={doc} reload={reload} onView={onView} />
          <ChangeHistory doc={doc} />
          <DecisionsManager doc={doc} reload={reload} />
        </div>
      )}
      {wsOpen && (
        <ReviewWorkspace doc={doc} clientId={doc.client_id} role="admin" onClose={() => setWsOpen(false)} />
      )}
    </div>
  );
}

function CreateDocumentForm({ title, setTitle, category, setCategory, onSave }) {
  return (
    <div className="flex flex-wrap items-end gap-2 p-4 rounded-xl border mb-4 bg-white" style={{ borderColor: "#E5E7EB" }} data-testid="document-form">
      <div className="flex-1 min-w-[200px]">
        <label className="text-sm text-[#6B7280] mb-1 block">Title</label>
        <input data-testid="document-title-input" value={title} onChange={(e) => setTitle(e.target.value)} className={`${fieldCls} w-full`} style={{ borderColor: "#E5E7EB" }} />
      </div>
      <div>
        <label className="text-sm text-[#6B7280] mb-1 block">Category</label>
        <select data-testid="document-category-select" value={category} onChange={(e) => setCategory(e.target.value)} className={fieldCls} style={{ borderColor: "#E5E7EB" }}>
          {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>
      <button data-testid="document-save-button" onClick={onSave} className="rounded-lg px-4 py-2 font-semibold text-white text-sm" style={{ backgroundColor: "#0A1F44" }}>Save</button>
    </div>
  );
}

function DocumentsTable({ docs, filtered, reload, onView }) {
  return (
    <div className="rounded-xl border overflow-hidden bg-white" style={{ borderColor: "#E5E7EB" }}>
      <div className="grid grid-cols-12 px-4 py-3 text-xs font-semibold uppercase tracking-wider" style={{ backgroundColor: "#0A1F44", color: "#fff" }}>
        <div className="col-span-3">Title</div>
        <div className="col-span-2">Category</div>
        <div className="col-span-3">Status</div>
        <div className="col-span-2">Portals</div>
        <div className="col-span-1 text-center">Main</div>
        <div className="col-span-1 text-center">Summary</div>
      </div>
      {(() => {
        if (docs.length === 0) return <p className="text-center py-12 text-[#6B7280]">No documents. Create one above.</p>;
        if (filtered.length === 0) return <p className="text-center py-12 text-[#6B7280]" data-testid="admin-doc-no-match">No documents match your search.</p>;
        return filtered.map((d) => <DocumentRow key={d.id} doc={d} reload={reload} onView={onView} />);
      })()}
    </div>
  );
}

function useCreateDocument(active, loadDocs) {
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const addDoc = async () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    await api.post(`/clients/${active}/documents`, { title, category, status: "Not Started" });
    toast.success("Document created");
    setTitle(""); setCategory(CATEGORIES[0]); setAdding(false); loadDocs();
  };
  return { adding, setAdding, title, setTitle, category, setCategory, addDoc };
}

export default function AdminDocuments() {
  const { clients, active, setActive, status: clientsStatus, error: clientsError, retry: retryClients } = useActiveClients();
  const [docs, setDocs] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const loadDocs = useCallback(() => { if (active) api.get(`/clients/${active}/documents`).then(({ data }) => setDocs(data)); }, [active]);
  useEffect(() => { loadDocs(); }, [loadDocs]);

  const create = useCreateDocument(active, loadDocs);
  const filtered = filterDocs(docs, search, filterCat, filterStatus);

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold mb-1">Documents</h1>
      <p className="text-[#6B7280] text-sm mb-6">Manage documents, files and decisions per client.</p>

      <ClientTabs clients={clients} active={active} onChange={setActive} status={clientsStatus} error={clientsError} onRetry={retryClients} />

      {active && (
        <>
          <ReviewWindows clientId={active} />
          <DocSearchBar search={search} setSearch={setSearch} category={filterCat} setCategory={setFilterCat}
            status={filterStatus} setStatus={setFilterStatus} testPrefix="admin-doc" />
          <div className="flex justify-between items-center mb-3">
            <p className="text-sm text-[#6B7280]" data-testid="admin-doc-count">{filtered.length} of {docs.length} document(s)</p>
            <button data-testid="add-document-button" onClick={() => create.setAdding((a) => !a)}
              className="flex items-center gap-2 rounded-lg px-4 py-2 font-semibold text-white text-sm" style={{ backgroundColor: "#F97316" }}>
              <Plus size={16} /> Create Document
            </button>
          </div>

          {create.adding && (
            <CreateDocumentForm title={create.title} setTitle={create.setTitle} category={create.category} setCategory={create.setCategory} onSave={create.addDoc} />
          )}

          <DocumentsTable docs={docs} filtered={filtered} reload={loadDocs} onView={setViewing} />
        </>
      )}

      {viewing && <DocViewer file={viewing.file} title={viewing.title} allowDownload onClose={() => setViewing(null)} />}
    </div>
  );
}
